import differenceInDays from 'date-fns/difference_in_calendar_days'
import filter from 'lodash/filter'
import find from 'lodash/find'
import flatMap from 'lodash/flatMap'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import parseDate from 'date-fns/parse'
import reduce from 'lodash/reduce'
import size from 'lodash/size'
import some from 'lodash/some'
import sortBy from 'lodash/sortBy'
import {createSelector} from 'reselect'

import {hasLength} from './helpers'
import {isActiveOrder} from '../components/Event/utils'
import {CategoryOrder} from './enumTS'
import {
  DAY_CUTOFF_TO_REQUEST_EXTENSION,
  DAY_CUTOFF_TO_RETURN_PURCHASE,
  TRANSACTION_TYPE_BUY,
} from '../const'

import * as user from './user'
// limited exception to rule as user id is required in many selectors and is superfluous to keep passing in userId

// Get

export const details = (state) => get(state, 'eventMgr.details')
export const eventDate = (state) => get(state, 'eventMgr.details.event.date')
export const eventId = (state) => get(state, 'eventMgr.details.event.id')
export const eventLooks = (state) => get(state, 'eventMgr.details.event.eventLooks')
export const eventName = (state) => get(state, 'eventMgr.details.event.name')
export const currenEventOwnerId = (state) => get(state, 'eventMgr.details.event.ownerId')
export const formIsProcessing = (state) => get(state, 'eventMgr.formIsProcessing')
export const hasError = (state) => get(state, 'eventMgr.hasError')
export const orders = (state) => get(state, 'eventMgr.details.orders')
export const successMsgBarVisible = (state) => get(state, 'eventMgr.successMsgBarVisible')
export const currentPageDetailsOrderId = (state) =>
  get(state, 'eventMgr.currentPageDetails.orderId')

// Helpers

export const getAccountCreatedStatus = (order) => get(order, 'accountCreated')
export const getArrivalDate = (order) => get(order, 'arrivalDate')
export const getEmailAddress = (order) => get(order, 'emailAddress')
export const getEventLookId = (look) => get(look, 'id')
export const getExpectedShipDate = (order) => get(order, 'expectedShipDate')
export const getIsEventCreatorStatus = (order) => get(order, 'isEventCreator')
export const getIsHto = (order) => get(order, 'homeTryOn')
export const getIsShipped = (order) => get(order, 'isShipped')
export const getReleasedShipmentsExist = (order) => get(order, 'releasedShipmentsExist')
export const getEventId = (order) => get(order, 'eventId')
export const getLatestFreeExchangeDate = (order) => get(order, 'latestFreeExchangeDate')
export const getOrderId = (order) => get(order, 'id')
export const getOrderIsCompletedStatus = (order) => get(order, 'orderCompleted')
export const getOrderIsCancelledStatus = (order) => get(order, 'cancelled')
export const getOrderLookProducts = (order) => get(order, 'orderLookProducts')
export const getOrderTypeExchange = (order) => get(order, 'exchange')
export const getOrderTypeHomeTryOn = (order) => get(order, 'homeTryOn')
export const getOrderTypePurchase = (order) => get(order, 'purchase')
export const getPayerId = (order) => get(order, 'payerId')
export const getPaymentStatus = (order) => get(order, 'paymentStatus')
export const getReturnByDate = (order) => get(order, 'returnByDate')
export const getShipmentId = (order) => get(order, 'shipmentId')
export const getShipmentTrackingLinks = (order) => get(order, 'shipmentTrackingLinks')
export const getShippingAddress1 = (order) => get(order, 'shippingAddress1')
export const getShippingAddress2 = (order) => get(order, 'shippingAddress2')
export const getShippingCity = (order) => get(order, 'shippingCity')
export const getShippingFirstName = (order) => get(order, 'shippingFirstName')
export const getShippingLastName = (order) => get(order, 'shippingLastName')
export const getShippingState = (order) => get(order, 'shippingState')
export const getShippingZip = (order) => get(order, 'shippingZip')
export const getSizesSubmittedStatus = (order) => get(order, 'sizesSubmitted')
export const getUserFirstName = (order) => get(order, 'userFirstName')
export const getUserId = (order) => get(order, 'userId')
export const getUserLastName = (order) => get(order, 'userLastName')
export const getWarehouseCode = (order) => get(order, 'warehouseCode')

// Details By OrderId

export const orderByOrderId = (state, id) => find(orders(state), {id})
export const accountCreatedStatusByOrderId = (state, id) =>
  getAccountCreatedStatus(orderByOrderId(state, id))
export const arrivalDateByOrderId = (state, id) => getArrivalDate(orderByOrderId(state, id))
export const emailAddressByOrderId = (state, id) => getEmailAddress(orderByOrderId(state, id))
export const eventIdByOrderId = (state, id) => getEventId(orderByOrderId(state, id))
export const expectedShipDateByOrderId = (state, id) =>
  getExpectedShipDate(orderByOrderId(state, id))
export const isEventCreatorOrderByOrderId = (state, id) =>
  getIsEventCreatorStatus(orderByOrderId(state, id))
export const isHtoByOrderId = (state, id) => getIsHto(orderByOrderId(state, id))
export const isShippedByOrderId = (state, id) => getIsShipped(orderByOrderId(state, id))
export const hasReleasedShipmentsByOrderId = (state, id) =>
  getReleasedShipmentsExist(orderByOrderId(state, id))
export const latestFreeExchangeDateByOrderId = (state, id) =>
  getLatestFreeExchangeDate(orderByOrderId(state, id))
export const orderIdById = (state, id) => id
export const orderIsCompletedStatusByOrderId = (state, id) =>
  getOrderIsCompletedStatus(orderByOrderId(state, id))
export const orderIsCancelledStatusByOrderId = (state, id) =>
  getOrderIsCancelledStatus(orderByOrderId(state, id))
export const orderLookProductsByOrderId = (state, id) =>
  getOrderLookProducts(orderByOrderId(state, id))
export const orderTypeExchangeByOrderId = (state, id) =>
  getOrderTypeExchange(orderByOrderId(state, id))
export const orderTypeHomeTryOnByOrderId = (state, id) =>
  getOrderTypeHomeTryOn(orderByOrderId(state, id))
export const orderTypePurchaseByOrderId = (state, id) =>
  getOrderTypePurchase(orderByOrderId(state, id))
export const payerIdByOrderId = (state, id) => getPayerId(orderByOrderId(state, id))
export const paymentIsCompletedStatusByOrderId = (state, id) =>
  getPaymentStatus(orderByOrderId(state, id))
export const returnByDateByOrderId = (state, id) => getReturnByDate(orderByOrderId(state, id))
export const shipmentIdByOrderId = (state, id) => getShipmentId(orderByOrderId(state, id))
export const shipmentTrackingLinksByOrderId = (state, id) =>
  getShipmentTrackingLinks(orderByOrderId(state, id))
export const shippingAddress1ByOrderId = (state, id) =>
  getShippingAddress1(orderByOrderId(state, id))
export const shippingAddress2ByOrderId = (state, id) =>
  getShippingAddress2(orderByOrderId(state, id))
export const shippingCityByOrderId = (state, id) => getShippingCity(orderByOrderId(state, id))
export const shippingFirstNameByOrderId = (state, id) =>
  getShippingFirstName(orderByOrderId(state, id))
export const shippingLastNameByOrderId = (state, id) =>
  getShippingLastName(orderByOrderId(state, id))
export const shippingStateByOrderId = (state, id) => getShippingState(orderByOrderId(state, id))
export const shippingZipByOrderId = (state, id) => getShippingZip(orderByOrderId(state, id))
export const sizesSubmittedStatusByOrderId = (state, id) =>
  getSizesSubmittedStatus(orderByOrderId(state, id))
export const userFirstNameByOrderId = (state, id) => getUserFirstName(orderByOrderId(state, id))
export const userIdByOrderId = (state, id) => getUserId(orderByOrderId(state, id))
export const userLastNameByOrderId = (state, id) => getUserLastName(orderByOrderId(state, id))
export const warehouseCodeByOrderId = (state, id) => getWarehouseCode(orderByOrderId(state, id))

// Details By UserId

export const orderByUserId = (state, userId) => find(orders(state), {userId})
export const orderIdByUserId = (state, userId) => getOrderId(orderByUserId(state, userId))
export const orderIsCancelledStatusByUserId = (state, userId) =>
  getOrderIsCancelledStatus(orderByUserId(state, userId))

// Look Details

export const lookByParticipantId = (state, userId) =>
  find(eventLooks(state), {participants: [{id: userId}]})
export const lookIdByParticipantId = (state, orderId) =>
  getEventLookId(lookByParticipantId(state, userIdByOrderId(state, orderId)))

// Computed

export const hasDetails = createSelector(details, hasLength)

export const isParticipantOrderAndCurrentUserIsEventOwner = createSelector(
  isEventCreatorOrderByOrderId,
  userIdByOrderId,
  user.id,
  (isEventCreatorOrder, orderUserId, userId) =>
    isEqual(isEventCreatorOrder, false) && !isEqual(orderUserId, userId),
)

export const isParticipantOrderAndEventOwnerIsPaying = createSelector(
  isEventCreatorOrderByOrderId,
  payerIdByOrderId,
  userIdByOrderId,
  (isEventCreator, payerId, userId) => !isEventCreator && !isEqual(payerId, userId),
)

export const orderAndPaymentAreNotCompleted = createSelector(
  orderIsCompletedStatusByOrderId,
  paymentIsCompletedStatusByOrderId,
  (orderIsComplete, paymentIsComplete) => !orderIsComplete && !paymentIsComplete,
)

export const canCancelOrder = createSelector(
  isParticipantOrderAndCurrentUserIsEventOwner,
  orderAndPaymentAreNotCompleted,
  (isParticipantOrder, orderIsNotPaid) => isParticipantOrder && orderIsNotPaid,
)

export const canRequestExtension = createSelector(
  eventDate,
  orderTypeExchangeByOrderId,
  (date, isExchange) =>
    !isExchange && differenceInDays(new Date(), parseDate(date)) < DAY_CUTOFF_TO_REQUEST_EXTENSION,
)

export const canReturnPurchase = createSelector(
  orderTypePurchaseByOrderId,
  arrivalDateByOrderId,
  (isPurchase, arrivalDate) =>
    isPurchase &&
    differenceInDays(new Date(), parseDate(arrivalDate)) < DAY_CUTOFF_TO_RETURN_PURCHASE,
)

export const nonHtoEventLooks = createSelector(eventLooks, (looks) => filter(looks, {isHto: false}))

export const orderIdIsValid = createSelector(orderIdById, orders, (id, $orders) =>
  some($orders, {id}),
)

export const orderLookProductsPriceSum = createSelector(
  orderLookProductsByOrderId,
  (orderLookProducts) =>
    reduce(
      orderLookProducts,
      (sum, look) =>
        sum +
        parseInt(
          look.transactionType === TRANSACTION_TYPE_BUY
            ? look.product.buyPrice
            : look.product.rentPrice,
          10,
        ),
      0,
    ),
)

export const orderLookProductsSortedByCategory = createSelector(
  orderLookProductsByOrderId,
  (items) => sortBy(items, [(item) => CategoryOrder[item.product.category]]),
)

export const activeEventCreatorOrders = createSelector(orders, ($orders) =>
  filter($orders, (order) => order.isEventCreator && isActiveOrder(order)),
)

export const activeParticipantOrders = createSelector(orders, ($orders) =>
  filter($orders, (order) => !order.isEventCreator && isActiveOrder(order)),
)

export const shouldShowParticipantOrders = createSelector(
  activeParticipantOrders,
  ($participantOrders) => some($participantOrders, (order) => isActiveOrder(order)),
)

export const hasParticipantOrders = createSelector(activeParticipantOrders, hasLength)

export const hasTrackingLinks = createSelector(shipmentTrackingLinksByOrderId, hasLength)

export const getAllUserOrders = createSelector(user.id, orders, (userId, $orders) =>
  filter($orders, {userId}),
)

export const getOrderStatusText = createSelector(
  orderIsCancelledStatusByOrderId,
  orderIsCompletedStatusByOrderId,
  (isCancelled, isCompleted) => {
    let statusText
    if (isCancelled) statusText = 'Cancelled'
    else if (isCompleted) statusText = 'Complete'
    else statusText = 'Incomplete'
    return statusText
  },
)

export const userIsEventCreator = createSelector(getAllUserOrders, (userOrders) =>
  some(userOrders, 'isEventCreator'),
)

export const htoEventLook = createSelector(eventLooks, (looks) => find(looks, {isHto: true}))

export const htoEstimatedDelivery = createSelector(htoEventLook, (look) =>
  get(look, 'hto.arrivalDateFormatted'),
)

export const userExchangeOrders = createSelector(getAllUserOrders, (userOrders) =>
  filter(userOrders, {exchange: true}),
)

export const hasExchangeOrders = createSelector(userExchangeOrders, hasLength)

export const userHtoOrder = createSelector(getAllUserOrders, (userOrders) =>
  find(userOrders, {homeTryOn: true}),
)

export const userPurchaseOrder = createSelector(getAllUserOrders, (userOrders) =>
  find(userOrders, {purchase: true}),
)

export const userRentalOrder = createSelector(getAllUserOrders, (userOrders) =>
  find(userOrders, {homeTryOn: false, rental: true, exchange: false}),
)

export const numberOfUserOrders = createSelector(getAllUserOrders, size)

export const canUpdateShippingAddress = createSelector(
  orderTypeExchangeByOrderId,
  orderTypeHomeTryOnByOrderId,
  orderTypePurchaseByOrderId,
  (isExchange, isHto, isPurchase) => !isExchange && !isHto && !isPurchase,
  // rental won't quite work here as a rental can also be a HTO or Exchange
)

export const currentUserIsEventCreator = createSelector(orders, user.id, ($orders, $userId) =>
  isEqual(get(find($orders, {isEventCreator: true}), 'userId'), $userId),
)

export const isCurrentUserTheEventOwner = createSelector(
  currenEventOwnerId,
  user.id,
  (eventOwnerId, $userId) => isEqual(eventOwnerId, $userId),
)

export const currentUserIsNotEventCreatorAndEventCreatorPaid = createSelector(
  currentUserIsEventCreator,
  payerIdByOrderId,
  userIdByOrderId,
  (isEventCreator, payerId, userId) => !isEventCreator && !isEqual(payerId, userId),
)

export const exchangeOrderLookProductIds = createSelector(userExchangeOrders, (exchanges) =>
  flatMap(exchanges, (exchange) => exchange.orderLookProducts.map((product) => product.id)),
)
